import { Link } from 'react-router-dom'
import { useReportSettings } from '../../hooks/useReports'
import { describeNextRun } from '../../components/admin/flowBilling/nextRun'
import { getUnitAbbreviation } from '../../utils/unitConversionUtils'
import { UnitCategoryId } from '../../common/enums'

/**
 * How your report is produced — read-only.
 *
 * The district being billed is entitled to know which group is reported, which
 * timezone decides where their month starts and ends, which unit their volumes
 * are stated in and when the report runs. They are not entitled to change any
 * of it: the recipients and the schedule are the platform operator's, for the
 * same reason a customer does not edit their own invoice.
 *
 * Everything here is a fact about the configuration, so it renders as a
 * definition list rather than as disabled form controls. A greyed-out input
 * invites a click that will never work; a plain value does not pretend.
 */

const ORDINAL = (n: number) => {
  const suffix = n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th'
  return `${n}${suffix}`
}

const pad = (n: number) => String(n).padStart(2, '0')

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="mt-1 sm:mt-0 sm:col-span-2">
        <span className="text-sm text-gray-900">{value}</span>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </dd>
    </div>
  )
}

export default function ReportSettingsPage() {
  const { data: settings, isLoading, isError } = useReportSettings()

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading…</div>
  }

  // A 404 here is an answer, not a fault: no report is configured for this
  // user's groups, which the Reports list already explains.
  if (isError || !settings) {
    return (
      <div className="p-6">
        <Link to="/reports/flowmeter" className="text-sm text-blue-600 hover:underline">
          ‹ Back to reports
        </Link>
        <p className="mt-4 text-sm text-gray-600">
          No report is configured for your groups.
        </p>
      </div>
    )
  }

  const unit = getUnitAbbreviation(UnitCategoryId.Volume, settings.unitId) || String(settings.unitId)
  const recipientCount =
    settings.recipients.disabled > 0
      ? `${settings.recipients.enabled} enabled · ${settings.recipients.disabled} disabled`
      : `${settings.recipients.enabled} enabled`

  return (
    <div className="p-6 max-w-3xl">
      <Link to="/reports/flowmeter" className="text-sm text-blue-600 hover:underline">
        ‹ Back to reports
      </Link>

      <h1 className="mt-4 text-2xl font-bold text-gray-900">Report settings</h1>
      <p className="mt-1 text-sm text-gray-600">
        How your monthly water volume report is produced.
      </p>

      {!settings.editable && (
        <div className="mt-4 rounded border border-blue-200 bg-blue-50 p-4 text-sm text-gray-700">
          You have read-only access to this report. Contact AtCreate to change the schedule or the
          recipient list.
        </div>
      )}

      <dl className="mt-6 divide-y divide-gray-200 border-t border-b border-gray-200">
        <Row label="Group" value={settings.groupName} />
        <Row
          label="Status"
          value={settings.enabled ? 'On' : 'Off'}
          hint={settings.enabled ? undefined : 'Nothing is generated or sent while this is off.'}
        />
        <Row
          label="Reporting timezone"
          value={settings.timezone}
          hint="Decides where each month starts and ends, and every timestamp on the report."
        />
        <Row label="Volume unit" value={`${unit}, ${settings.decimalPlaces} decimal places`} />
        <Row
          label="Period"
          value={
            settings.periodType === 'rolling_days'
              ? `Rolling window of ${settings.periodDays} days`
              : 'Previous calendar month'
          }
        />
        <Row
          label="Generated"
          value={`${ORDINAL(settings.generationDay)} of the month at ${pad(settings.generationHour)}:${pad(settings.generationMinute)}`}
          hint="The delay past midnight lets readings that arrive late over SigFox land before the month is totalled."
        />
        <Row label="Next run" value={describeNextRun(settings) || '—'} />
        <Row
          label="Meters with no readings"
          value={settings.includeDevicesWithoutReadings ? 'Reported as a zero row' : 'Left out'}
          hint={
            settings.includeDevicesWithoutReadings
              ? 'A meter that has gone quiet is a fact worth seeing, not a row to hide.'
              : undefined
          }
        />
        <Row label="Recipients" value={recipientCount} hint="Addresses are held by AtCreate." />
      </dl>
    </div>
  )
}
